import type { Device } from "@/types";
import type { LucideIcon } from "lucide-react";
import {
  BatteryCharging,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
} from "lucide-react";
import { isReaderPaired } from "@/lib/readerLabel";

/** How long telemetry is considered fresh (admin devices view). */
export const TELEMETRY_FRESH_MS = 20 * 60 * 60 * 1000;

export type TelemetryStatus = "gray" | "green" | "yellow" | "red";

export type SetupBadgeKind = "none" | "incomplete" | "revoked";

export type SetupMissingKind = "binding" | "pairing" | "assignment";

/** Minimal FreeKiosk /status shape the portal reads. */
export interface DeviceStatusPayload {
  success?: boolean;
  app_version?: string;
  data?: DeviceStatusData;
  timestamp?: number | string;
  /** Flat fallback when `data` is absent. */
  battery?: DeviceBatteryStatus;
  wifi?: DeviceWifiStatus;
  audio?: DeviceAudioStatus;
  storage?: DeviceUsageStatus;
  memory?: DeviceUsageStatus;
}

export interface DeviceBatteryStatus {
  level?: number;
  charging?: boolean;
}

export interface DeviceWifiStatus {
  signalLevel?: number;
}

export interface DeviceAudioStatus {
  volume?: number;
}

export interface DeviceUsageStatus {
  usedPercent?: number;
}

export interface DeviceStatusData {
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  battery?: DeviceBatteryStatus;
  wifi?: DeviceWifiStatus;
  audio?: DeviceAudioStatus;
  storage?: DeviceUsageStatus;
  memory?: DeviceUsageStatus;
}

/** Minimal GPS / location JSON the portal reads. */
export interface DeviceLocationPayload {
  success?: boolean;
  app_version?: string;
  data?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  };
  /** Flat fallback when `data` is absent. */
  latitude?: number;
  longitude?: number;
  accuracy?: number;
}

export type DeviceStatusInput = Pick<
  Device,
  | "software_id"
  | "last_login_at"
  | "last_device_report_at"
  | "device_status_payload"
  | "device_location_payload"
  | "house_id"
  | "is_revoked"
> & {
  reader?: Device["reader"];
};

export type DeviceCoordinates = {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
};

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

/** Battery Lucide icon from charge level (0–100) and optional charging flag. */
export function getBatteryIcon(
  level: number | null,
  charging: boolean | null = null,
): LucideIcon {
  if (charging === true) {
    return BatteryCharging;
  }
  if (level === null || level > 70) {
    return BatteryFull;
  }
  if (level > 40) {
    return BatteryMedium;
  }
  return BatteryLow;
}

function isPresentPayload(value: unknown): boolean {
  return value !== null && value !== undefined;
}

/** Prefer nested `.data`, otherwise the payload root (FreeKiosk shapes vary). */
export function getDeviceStatusData(
  payload: DeviceStatusPayload | null | undefined,
): Record<string, unknown> | null {
  const payloadRecord = asRecord(payload);
  if (!payloadRecord) {
    return null;
  }
  return asRecord(payloadRecord.data) ?? payloadRecord;
}

export function getDeviceStatusTimestamp(
  payload: DeviceStatusPayload | null | undefined,
): Date | null {
  const payloadRecord = asRecord(payload);
  if (!payloadRecord) {
    return null;
  }

  const timestamp = payloadRecord.timestamp;
  const numericTimestamp =
    typeof timestamp === "number"
      ? timestamp
      : typeof timestamp === "string"
        ? Number(timestamp)
        : Number.NaN;
  if (!Number.isFinite(numericTimestamp)) {
    return null;
  }

  const milliseconds =
    numericTimestamp < 1_000_000_000_000
      ? numericTimestamp * 1000
      : numericTimestamp;
  const date = new Date(milliseconds);
  return Number.isNaN(date.getTime()) ? null : date;
}

function readTelemetryRecord(
  data: Record<string, unknown> | null,
  key: string,
): Record<string, unknown> | null {
  if (!data) {
    return null;
  }
  return asRecord(data[key]);
}

export function readTelemetryNumber(
  data: Record<string, unknown> | null,
  section: string,
  key: string,
): number | null {
  const value = readTelemetryRecord(data, section)?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function readTelemetryBoolean(
  data: Record<string, unknown> | null,
  section: string,
  key: string,
): boolean | null {
  const value = readTelemetryRecord(data, section)?.[key];
  return typeof value === "boolean" ? value : null;
}

export function readDeviceCoordinates(
  payload: DeviceLocationPayload | null | undefined,
): DeviceCoordinates | null {
  const payloadRecord = asRecord(payload);
  if (!payloadRecord || payloadRecord.success === false) {
    return null;
  }

  const dataRecord = asRecord(payloadRecord.data) ?? payloadRecord;
  const latitude = dataRecord.latitude;
  const longitude = dataRecord.longitude;
  const accuracy = dataRecord.accuracy;

  if (
    typeof latitude !== "number" ||
    typeof longitude !== "number" ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }

  return {
    latitude,
    longitude,
    accuracyMeters:
      typeof accuracy === "number" && Number.isFinite(accuracy) && accuracy > 0
        ? accuracy
        : null,
  };
}

/** FreeKiosk /status payload was stored on the last report. */
export function hasDeviceStatusPayload(
  payload: DeviceStatusPayload | null | undefined,
): boolean {
  if (!isPresentPayload(payload)) {
    return false;
  }
  const record = asRecord(payload);
  if (!record) {
    return true;
  }
  if (Object.keys(record).length === 0) {
    return false;
  }
  if (record.success === false) {
    return false;
  }
  return true;
}

/** Device GPS payload includes coordinates. */
export function hasGpsPayload(
  payload: DeviceLocationPayload | null | undefined,
): boolean {
  if (!isPresentPayload(payload)) {
    return false;
  }
  const record = asRecord(payload);
  if (!record) {
    return false;
  }
  if (record.success === false) {
    return false;
  }
  const data = asRecord(record.data) ?? record;
  return (
    typeof data.latitude === "number" && typeof data.longitude === "number"
  );
}

export function isTelemetryComplete(device: DeviceStatusInput): boolean {
  return (
    hasDeviceStatusPayload(device.device_status_payload) &&
    hasGpsPayload(device.device_location_payload)
  );
}

export function isTelemetryRecent(
  lastReportAt: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (lastReportAt == null || lastReportAt === "") {
    return false;
  }
  const reportedAt = new Date(lastReportAt).getTime();
  if (Number.isNaN(reportedAt)) {
    return false;
  }
  return nowMs - reportedAt <= TELEMETRY_FRESH_MS;
}

export function getTelemetryStatus(
  device: DeviceStatusInput,
  nowMs: number = Date.now(),
): TelemetryStatus {
  const softwareId = device.software_id?.trim();
  if (!softwareId || device.last_login_at == null) {
    return "gray";
  }
  if (!isTelemetryRecent(device.last_device_report_at, nowMs)) {
    return "red";
  }
  if (isTelemetryComplete(device)) {
    return "green";
  }
  return "yellow";
}

export function telemetryStatusDotClass(status: TelemetryStatus): string {
  switch (status) {
    case "gray":
      return "bg-surface-400";
    case "green":
      return "bg-success-500";
    case "yellow":
      return "bg-warning-400";
    case "red":
      return "bg-error-500";
  }
}

export function isDeviceReady(device: DeviceStatusInput): boolean {
  return (
    device.is_revoked !== true &&
    Boolean(device.software_id?.trim()) &&
    Boolean(device.house_id) &&
    isReaderPaired(device.reader)
  );
}

export function getSetupBadgeKind(device: DeviceStatusInput): SetupBadgeKind {
  if (device.is_revoked === true) {
    return "revoked";
  }
  if (isDeviceReady(device)) {
    return "none";
  }
  return "incomplete";
}

export type DeviceAppVersionLabels = {
  unknown: string;
  legacy: string;
};

function readAppVersionFromPayload(
  payload: DeviceStatusPayload | DeviceLocationPayload | null | undefined,
): string | null {
  const version = asRecord(payload)?.app_version;
  if (typeof version !== "string") {
    return null;
  }
  const trimmed = version.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function compareAppVersionLabels(
  a: string,
  b: string,
  labels: DeviceAppVersionLabels,
): number {
  if (a === labels.unknown) {
    return 1;
  }
  if (b === labels.unknown) {
    return -1;
  }
  if (a === labels.legacy) {
    return 1;
  }
  if (b === labels.legacy) {
    return -1;
  }
  return b.localeCompare(a, undefined, { numeric: true });
}

/** Resolve app version for device tables from telemetry payloads. */
export function formatDeviceAppVersion(
  device: Pick<
    Device,
    | "last_device_report_at"
    | "device_status_payload"
    | "device_location_payload"
  >,
  labels: DeviceAppVersionLabels,
): string {
  const fromLocation = readAppVersionFromPayload(device.device_location_payload);
  if (fromLocation) {
    return fromLocation;
  }
  const fromStatus = readAppVersionFromPayload(device.device_status_payload);
  if (fromStatus) {
    return fromStatus;
  }
  if (device.last_device_report_at == null || device.last_device_report_at === "") {
    return labels.unknown;
  }
  return labels.legacy;
}

export function getSetupMissingItems(
  device: DeviceStatusInput,
): SetupMissingKind[] {
  const missing: SetupMissingKind[] = [];
  if (!device.software_id?.trim()) {
    missing.push("binding");
  }
  if (!isReaderPaired(device.reader)) {
    missing.push("pairing");
  }
  if (!device.house_id) {
    missing.push("assignment");
  }
  return missing;
}

export type SetupCompletenessFilter =
  | "all"
  | "complete"
  | "incomplete"
  | "revoked";

export type ShelvedFilter = "all" | "in_service" | "shelved";

export function matchesSetupCompletenessFilter(
  device: DeviceStatusInput,
  filter: SetupCompletenessFilter,
): boolean {
  switch (filter) {
    case "all":
      return true;
    case "complete":
      return isDeviceReady(device);
    case "incomplete":
      return getSetupBadgeKind(device) === "incomplete";
    case "revoked":
      return device.is_revoked === true;
  }
}

export function matchesShelvedFilter(
  device: Pick<Device, "is_shelved">,
  filter: ShelvedFilter,
): boolean {
  switch (filter) {
    case "all":
      return true;
    case "in_service":
      return device.is_shelved !== true;
    case "shelved":
      return device.is_shelved === true;
  }
}

export function matchesIncompleteMissingFilters(
  device: DeviceStatusInput,
  filters: readonly SetupMissingKind[],
): boolean {
  if (filters.length === 0) {
    return true;
  }
  const missing = getSetupMissingItems(device);
  return filters.every((kind) => missing.includes(kind));
}

export type TelemetryStatusFilter = "all" | TelemetryStatus;

export function matchesTelemetryStatusFilter(
  device: DeviceStatusInput,
  filter: TelemetryStatusFilter,
  nowMs: number = Date.now(),
): boolean {
  if (filter === "all") {
    return true;
  }
  return getTelemetryStatus(device, nowMs) === filter;
}

export function matchesAppVersionFilters(
  device: Pick<
    Device,
    | "last_device_report_at"
    | "device_status_payload"
    | "device_location_payload"
  >,
  selectedVersions: readonly string[],
  labels: DeviceAppVersionLabels,
): boolean {
  if (selectedVersions.length === 0) {
    return true;
  }
  return selectedVersions.includes(formatDeviceAppVersion(device, labels));
}
